import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/hooks/useConfig";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function DPBrandsPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { getItems: getCfgItems } = useConfig();
  const brandsFlagOn = (getCfgItems("feature_flags") || []).some(i => i.key === "BRANDS" && i.value === "true");
  useEffect(() => { if (brandsFlagOn) navigate("/brands", { replace: true }); }, [brandsFlagOn, navigate]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [ownerType, setOwnerType] = useState("skippi");
  const { user } = useAuth();
  const { getValues } = useConfig();
  const ownerTypes = getValues("dp_brand_owner_types");

  const perms = user?.permissions || [];
  const canManage = perms.includes("dp.brands.manage") || perms.includes("dp.admin") || perms.includes("admin.full_access") || (user?.roles || []).some(r => ["Admin", "admin"].includes(r));

  const load = () => {
    setLoading(true);
    api.get("/dp/brands").then(r => setBrands(r.data || [])).catch(e => toast.error(e.response?.data?.detail || "Failed")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editId) {
        await api.put(`/dp/brands/${editId}`, { name, owner_type: ownerType });
      } else {
        await api.post("/dp/brands", { name, owner_type: ownerType });
      }
      toast.success(editId ? "Brand updated" : "Brand created");
      setShowForm(false); setEditId(null); setName(""); setOwnerType("skippi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this brand?")) return;
    try {
      const r = await api.delete(`/dp/brands/${id}`);
      toast.success(r.data.action === "set_inactive" ? "Brand set inactive (in use)" : "Brand deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const startEdit = (b) => { setEditId(b._id); setName(b.name); setOwnerType(b.owner_type); setShowForm(true); };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="dp-brands-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-zinc-900">DP Brands</h1>
        {canManage && <Button size="sm" onClick={() => { setShowForm(true); setEditId(null); setName(""); setOwnerType("skippi"); }} data-testid="add-brand-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Brand</Button>}
      </div>
      {showForm && canManage && (
        <div className="border border-zinc-200 rounded-sm p-4 mb-4 space-y-3" data-testid="brand-form">
          <Input placeholder="Brand name (e.g. SKIPPI)" value={name} onChange={e => setName(e.target.value)} className="h-8 text-xs" data-testid="input-brand-name" />
          <Select value={ownerType} onValueChange={setOwnerType}>
            <SelectTrigger className="h-8 text-xs" data-testid="select-owner-type"><SelectValue /></SelectTrigger>
            <SelectContent>{ownerTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} data-testid="save-brand-btn">{editId ? "Update" : "Create"}</Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}
      {loading ? <p className="text-xs text-zinc-400 py-8 text-center">Loading...</p> : (
        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-3 py-2 font-medium text-zinc-600">Brand</th>
              <th className="text-left px-3 py-2 font-medium text-zinc-600">Type</th>
              <th className="text-left px-3 py-2 font-medium text-zinc-600">Status</th>
              {canManage && <th className="px-3 py-2"></th>}
            </tr></thead>
            <tbody>
              {brands.map(b => (
                <tr key={b._id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`brand-${b._id}`}>
                  <td className="px-3 py-2 font-medium text-zinc-800">{b.name}</td>
                  <td className="px-3 py-2 text-zinc-600 capitalize">{b.owner_type}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${b.status === "active" ? "text-emerald-600 border-emerald-200" : "text-zinc-400 border-zinc-200"}`}>{b.status}</Badge></td>
                  {canManage && <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" className="h-6 px-2 mr-1" onClick={() => startEdit(b)}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-red-500" onClick={() => handleDelete(b._id)}><Trash2 className="w-3 h-3" /></Button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
